import React from 'react'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import { DashboardMetricTileGrid } from '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.component'
import {
  metricDataToItemPropsWithLayoutClass,
  type DashboardMetricItemData,
} from '@utils/dashboardMetrics.util'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  AR_BUCKETS,
  AR_BUCKET_COLORS,
  CLAIMS_CHART_COLORS,
  CLAIMS_LEGEND_LABELS,
  FINANCE_GROSS_NET_BAR_COLORS,
  FINANCE_GROSS_NET_LINE_COLORS,
  TREATMENT_VALUE_COLORS,
} from '@constants/Dashboard/financeDashboard.constants'
import type { FinancePracticeRevenueCard } from '../../../../types/financeDashboard.types'
import { financeTrendLine } from '@utils/dashboardFinance.util'

export const FINANCE_METRIC_TILE_CLASS =
  'dashboard-metric-tile-box dashboard-metric-item'

export type FinanceMetricTileItemProps = React.ComponentProps<
  typeof DashboardMetricItem
>

export function FinanceMetricTileItem({
  className,
  ...props
}: FinanceMetricTileItemProps): React.ReactElement {
  return (
    <DashboardMetricItem
      {...props}
      className={[FINANCE_METRIC_TILE_CLASS, className]
        .filter(Boolean)
        .join(' ')}
    />
  )
}

export function FinanceMetricTileGrid({
  items,
  columns = 4,
}: {
  items: FinanceMetricTileItemProps[]
  columns?: 2 | 3 | 4
}): React.ReactElement {
  return (
    <div
      className={`dashboard-metric-tile-grid dashboard-metric-tile-grid--cols-${columns}`}
    >
      {items.map((item, index) => (
        <FinanceMetricTileItem
          key={item.leftLabel ?? `metric-${index}`}
          {...item}
        />
      ))}
    </div>
  )
}

export function FinanceMetricTileFromData(
  data: DashboardMetricItemData,
): React.ReactElement {
  const props = metricDataToItemPropsWithLayoutClass(
    data,
    FINANCE_METRIC_TILE_CLASS,
  )
  return <DashboardMetricItem {...props} />
}

type ProdCollMetrics = {
  gross: string
  net: string
  adjustment: string
  rate: string
}

export function FinancePracticeRevenueTiles({
  cards,
  range,
}: {
  cards: FinancePracticeRevenueCard[]
  range: RangeKey
}): React.ReactElement {
  const metrics: DashboardMetricItemData[] = cards.map((c) => ({
    id: c.leftLabel,
    leftLabel: c.leftLabel,
    rightLabel: c.rightLabel,
    value: c.value,
    footer: financeTrendLine(range, c.footerPct, c.footerAmt),
    footerTrend: c.footerUp ? 'up' : 'down',
  }))
  return <DashboardMetricTileGrid metrics={metrics} columns={4} />
}

export function FinanceProductionMetricsTiles({
  prodColl,
}: {
  prodColl: ProdCollMetrics
}): React.ReactElement {
  const metrics: DashboardMetricItemData[] = [
    { id: 'gross-prod', leftLabel: 'Gross production', value: prodColl.gross },
    { id: 'net-prod', leftLabel: 'Net production', value: prodColl.net },
    { id: 'adjustment', leftLabel: 'Adjustment', value: prodColl.adjustment },
    { id: 'prod-rate', leftLabel: 'Production rate', value: prodColl.rate },
  ]
  return <DashboardMetricTileGrid metrics={metrics} columns={4} />
}

export function FinanceCollectionMetricsTiles({
  prodColl,
}: {
  prodColl: ProdCollMetrics
}): React.ReactElement {
  const metrics: DashboardMetricItemData[] = [
    {
      id: 'gross-coll',
      leftLabel: 'Gross collection',
      value: prodColl.gross,
    },
    { id: 'net-coll', leftLabel: 'Net collection', value: prodColl.net },
    { id: 'adjustment', leftLabel: 'Adjustment', value: prodColl.adjustment },
    {
      id: 'coll-rate',
      leftLabel: 'Collection rate',
      value: prodColl.rate,
    },
  ]
  return <DashboardMetricTileGrid metrics={metrics} columns={4} />
}

export function FinanceHalfDonutMetricsTiles({
  metrics,
}: {
  metrics: DashboardMetricItemData[]
}): React.ReactElement {
  return <DashboardMetricTileGrid metrics={metrics} columns={2} />
}

const FINANCE_HALF_DONUT_LEGEND_LABELS: Record<string, string> = {
  Services: 'Service fee',
}

export function FinanceHalfDonutLegend({
  slices,
  order,
}: {
  slices: Array<{ name: string; color: string }>
  order?: readonly string[]
}): React.ReactElement {
  const orderedSlices = order
    ? order
        .map((name) => slices.find((slice) => slice.name === name))
        .filter((slice): slice is { name: string; color: string } => !!slice)
    : slices

  return (
    <div className="dashboard-finance-half-donut-legend">
      {orderedSlices.map((slice) => (
        <span
          key={slice.name}
          className="dashboard-finance-half-donut-legend-item"
        >
          <span
            className="dashboard-finance-half-donut-legend-dot"
            style={{ background: slice.color }}
          />
          {FINANCE_HALF_DONUT_LEGEND_LABELS[slice.name] ?? slice.name}
        </span>
      ))}
    </div>
  )
}

export function FinanceMetricTilesRow({
  metrics,
  columns = 4,
}: {
  metrics: DashboardMetricItemData[]
  columns?: 2 | 3 | 4
}): React.ReactElement {
  return <DashboardMetricTileGrid metrics={metrics} columns={columns} />
}

/** Figma: 50px bars with 5px gap between Gross/Net pairs (node 5462:119144). */
export const FINANCE_DUAL_BAR_CHART_PROPS = {
  barSize: 50,
  barGap: 5,
  barCategoryGap: '18%',
} as const

type FinancePanelHeaderProps = {
  trendLine?: string
  goalLabel: string
}

/** Trend + goal grouped for `DashboardSectionCard` `headerRight` (Figma title row). */
export function FinanceProductionSectionHeaderRight({
  trendLine,
  goalLabel,
}: FinancePanelHeaderProps): React.ReactElement {
  return (
    <div className="dashboard-finance-section-header-end-metrics">
      {trendLine ? (
        <span className="dashboard-finance-production-change">{trendLine}</span>
      ) : null}
      <span className="dashboard-finance-production-goal">{goalLabel}</span>
    </div>
  )
}

export function FinanceCollectionSectionHeaderRight({
  goalLabel,
}: {
  goalLabel: string
}): React.ReactElement {
  return (
    <div className="dashboard-finance-section-header-end-metrics">
      <span className="dashboard-finance-production-goal">{goalLabel}</span>
    </div>
  )
}

export function FinanceProductionPanelHeader({
  trendLine,
  goalLabel,
}: FinancePanelHeaderProps): React.ReactElement {
  return (
    <div className="dashboard-finance-production-header">
      {trendLine ? (
        <span className="dashboard-finance-production-change">{trendLine}</span>
      ) : (
        <span />
      )}
      <span className="dashboard-finance-production-goal">{goalLabel}</span>
    </div>
  )
}

/** Trend + goal on the section title row (Figma Production by procedure). */
export function FinanceProductionByProcedureHeader({
  trendLine,
  goalLabel,
}: FinancePanelHeaderProps): React.ReactElement {
  return (
    <div className="dashboard-finance-production-by-procedure-header">
      {trendLine ? (
        <span className="dashboard-finance-production-change">{trendLine}</span>
      ) : null}
      <span className="dashboard-finance-production-goal">{goalLabel}</span>
    </div>
  )
}

/** Trend only on the section title row (Figma Performance Collection). */
export function FinanceCollectionTrendHeader({
  trendLine,
}: {
  trendLine?: string
}): React.ReactElement | null {
  if (!trendLine) return null
  return (
    <div className="dashboard-finance-collection-trend-header">
      <span className="dashboard-finance-production-change">{trendLine}</span>
    </div>
  )
}

export function FinanceCollectionPanelHeader({
  goalLabel,
}: {
  goalLabel: string
}): React.ReactElement {
  return (
    <div className="dashboard-finance-production-header dashboard-finance-production-header--goal-only">
      <span className="dashboard-finance-production-goal">{goalLabel}</span>
    </div>
  )
}

type FinanceSeriesLegendItem = {
  name: string
  color: string
}

type FinanceSeriesLegendProps = {
  series: readonly FinanceSeriesLegendItem[]
  className?: string
  itemClassName?: string
  dotClassName?: string
}

export function FinanceSeriesLegend({
  series,
  className = 'dashboard-finance-production-legend',
  itemClassName = 'dashboard-finance-production-legend-item',
  dotClassName = 'dashboard-finance-production-legend-icon circle',
}: FinanceSeriesLegendProps): React.ReactElement {
  return (
    <div className={className}>
      {series.map((item) => (
        <span key={item.name} className={itemClassName}>
          <span className={dotClassName} style={{ background: item.color }} />
          {item.name}
        </span>
      ))}
    </div>
  )
}

export function FinanceGrossNetBarLegend(): React.ReactElement {
  return (
    <FinanceSeriesLegend
      series={[
        { name: 'Gross', color: FINANCE_GROSS_NET_BAR_COLORS.gross },
        { name: 'Net', color: FINANCE_GROSS_NET_BAR_COLORS.net },
      ]}
    />
  )
}

export function FinanceGrossNetLineLegend({
  showGoal = false,
  grossLabel = 'Gross collection',
  netLabel = 'Net collection',
}: {
  showGoal?: boolean
  grossLabel?: string
  netLabel?: string
} = {}): React.ReactElement {
  return (
    <div className="dashboard-finance-production-legend">
      <span className="dashboard-finance-production-legend-item">
        <span
          className="dashboard-finance-production-legend-icon circle"
          style={{ background: FINANCE_GROSS_NET_LINE_COLORS.gross }}
        />
        {grossLabel}
      </span>
      <span className="dashboard-finance-production-legend-item">
        <span
          className="dashboard-finance-production-legend-icon circle"
          style={{ background: FINANCE_GROSS_NET_LINE_COLORS.net }}
        />
        {netLabel}
      </span>
      {showGoal ? (
        <span className="dashboard-finance-production-legend-item">
          <span className="dashboard-finance-production-legend-icon dash" />
          Goal
        </span>
      ) : null}
    </div>
  )
}

export function FinanceClaimsLegend(): React.ReactElement {
  return (
    <FinanceSeriesLegend
      series={CLAIMS_LEGEND_LABELS.map((label) => ({
        name: label,
        color: CLAIMS_CHART_COLORS[label],
      }))}
    />
  )
}

export function FinanceArAgingBarLegend(): React.ReactElement {
  return (
    <div className="dashboard-finance-production-legend">
      {AR_BUCKETS.map((bucket) => (
        <span key={bucket} className="dashboard-finance-production-legend-item">
          <span
            className="dashboard-finance-production-legend-icon circle"
            style={{ background: AR_BUCKET_COLORS[bucket] }}
          />
          {bucket}
        </span>
      ))}
    </div>
  )
}

const TREATMENT_VALUE_LEGEND_KEYS = [
  'Billed',
  'Collected',
  'Potential',
] as const

export function FinanceTreatmentValueBarLegend(): React.ReactElement {
  return (
    <div className="dashboard-finance-production-legend">
      {TREATMENT_VALUE_LEGEND_KEYS.map((key) => (
        <span key={key} className="dashboard-finance-production-legend-item">
          <span
            className="dashboard-finance-production-legend-icon circle"
            style={{ background: TREATMENT_VALUE_COLORS[key] }}
          />
          {key}
        </span>
      ))}
    </div>
  )
}
